import { effect, inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  CapturedImage,
  defaultPictureAcquisitionSteps,
  PictureAcquisitionStatus,
  PictureAcquisitionStep,
} from './models/picture-acquisition.model';
import { DialogComponent } from '../design-system/components/dialog/dialog.component';
import { RemoteConfigService } from '../remote-config/remote-config.service';
import { MobileModeService } from '../app-mobile-mode.service';

@Injectable({
  providedIn: 'root',
})
export class PictureAcquisitionService {
  acquisitionStatus = signal(PictureAcquisitionStatus.STOP);

  acquisitionSteps = signal<PictureAcquisitionStep[]>([]);

  acquisitionStepIndex = signal(0);

  capturedImages = signal(new Map<number, CapturedImage>());

  captureImageIndex: number | null = null;

  nbPictures = 2;

  canExit = false;

  private readonly mobileModeService = inject(MobileModeService);

  private readonly remoteConfig = inject(RemoteConfigService);

  private readonly dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      if (this.acquisitionStatus() === PictureAcquisitionStatus.STOP) {
        this.resetContext();
      }
    });
  }

  configAcqusitionSteps() {
    this.acquisitionSteps.set(defaultPictureAcquisitionSteps[this.nbPictures] || defaultPictureAcquisitionSteps[8]);
  }

  getCurrentStep() {
    return this.acquisitionSteps()[this.acquisitionStepIndex()];
  }

  nextStep() {
    if (this.acquisitionStepIndex() < this.acquisitionSteps().length) {
      this.acquisitionStepIndex.set(this.acquisitionStepIndex() + 1);
    }
  }

  saveCapturedImage(image: string) {
    const { category, name, view } = this.acquisitionSteps()[this.acquisitionStepIndex()];
    const captureImage: CapturedImage = {
      category,
      name,
      timestamp: new Date().toISOString(),
      image,
      view,
    };

    const capturedImagesMap = this.capturedImages();
    capturedImagesMap.set(this.acquisitionStepIndex(), captureImage);
    this.capturedImages.set(capturedImagesMap);
  }

  cancelAcquisition() {
    this.dialog.open(DialogComponent, {
      width: `${this.mobileModeService.isMobileMode() ? 80 : 30}vw`,
      panelClass: 'app-picture-acquisition-dialog',
      disableClose: true,
      data: {
        materialIcon: 'question_mark',
        body: 'CANCEL_CAPTURE_MESSAGE',
        confirmation: true,
        yesBtnColor: 'warn',
        noBtnColor: 'primary',
      },
    }).afterClosed().subscribe((answer: boolean) => {
      if (answer) {
        this.acquisitionStatus.set(PictureAcquisitionStatus.STOP);
      }
    });
  }

  isAllImagesCaptured() {
    return this.capturedImages().size === this.acquisitionSteps().length;
  }

  resetContext() {
    this.acquisitionStepIndex.set(0);
    this.capturedImages.set(new Map<number, CapturedImage>());
  }

  get searchActivated() {
    return ['all', 'search'].includes(this.remoteConfig.get('tablet_ub_action') as string);
  }
}
